<template>
  <div class="home-page">
    <client-only>
      <HomeSubHero :items="subHeroItems" />
    </client-only>

    <HomeVideo />
    <HomeFactoid v-bind="Factoids[0]" />
    <div id="autopilot"></div>
    <br />
    <HomeAutomateHR class="py-10 lg:py-20" />
    <HomeFactoid v-bind="Factoids[1]" />
    <div id="dedicated-manager" class="mb-16"></div>
    <br />
    <HomeDedicatedHr />
    <HomeHrmAvailable id="hrm-available" />
    <HomeFactoid v-bind="Factoids[2]" />
    <HomeHrAutopilotForEmployees />
    <HomeFactoid v-bind="Factoids[3]" />
    <div id="pricing-plans" class="flex justify-center">
      <client-only>
        <PricingPlans class="py-16" />
      </client-only>
    </div>
    <HomeHowLowPrices />
    <HomeBambeeSealRibbon />
    <HomeFaq />
    <HomeRibbonCTA />
    <SplashModals />
    <TheFooter />
    <CookiePolicyBanner />
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  useMeta,
  useRoute,
} from '@nuxtjs/composition-api';
import Cookie from 'js-cookie';
import HomeAutomateHR from '../components/HomeAutomateHR.vue';
import HomeSubHero from '../components/HomeSubHero';
import HomeVideo from '../components/HomeVideo';
import HomeFactoid from '../components/HomeFactoid';
import HomeDedicatedHr from '../components/HomeDedicatedHr';
import HomeHrmAvailable from '../components/HomeHrmAvailable';
import HomeHrAutopilotForEmployees from '../components/HomeHrAutopilotForEmployees';
import HomeHowLowPrices from '../components/HomeHowLowPrices';
import HomeBambeeSealRibbon from '../components/HomeBambeeSealRibbon';
import HomeFaq from '../components/HomeFaq';
import HomeRibbonCTA from '../components/HomeRibbonCTA';
import PricingPlans from '@/modules/InformationPages/Pricing/components/PricingPlans.vue';
import SplashModals from '@/modules/SplashPage/components/SplashModals.vue';
import useMatchMedia from '@/hooks/useMatchMedia';
import SubHeroIcons from '@/constants/SubHeroIcons';
import { Factoids } from '@/modules/HomePage/constants';
import CookieNames from '@/constants/CookieNames';
import TrackingPixel from '@/components/Marketing/TrackingPixel.vue';
import TheFooter from '@/components/Navigation/TheFooter.vue';
import CookiePolicyBanner from '@/modules/CookiePolicy/components/CookiePolicyBanner/CookiePolicyBanner.vue'


const META_TITLE =
  "Bambee is the HR Manager solution for America's small businesses";
const META_DESCRIPTION_SHORT =
  'Bambee is the leading outsourced HR solution for small to large businesses';
const META_DESCRIPTION =
  'Bambee is the leading outsourced HR solution for small to large businesses. You get a full range of services including compliance, training, report cards, and AI empowered HR.';

export default defineComponent({
  name: 'RootHomePage',
  components: {
    HomeAutomateHR,
    HomeBambeeSealRibbon,
    HomeDedicatedHr,
    HomeFactoid,
    HomeFaq,
    HomeHowLowPrices,
    HomeHrAutopilotForEmployees,
    HomeHrmAvailable,
    HomeRibbonCTA,
    HomeSubHero,
    HomeVideo,
    PricingPlans,
    SplashModals,
    TrackingPixel,
    TheFooter,
    CookiePolicyBanner,
  },
  layout: 'HomeSplash',
  setup() {
    const route = useRoute();
    const viewedPriceModel = ref(false);
    const matchMedia = useMatchMedia();
    const subHeroItems = computed(() => {
      const items = SubHeroIcons;

      return matchMedia.value.lg ? items : items.slice(0, -1);
    });
    const { title } = useMeta();
    title.value = META_TITLE;

    const bootIntercom = () => {
      /* eslint-disable */
      window.intercomSettings = {
        app_id: process.env.INTERCOM_APP_ID,
      };
      const w = window;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        const d = document;
        var i = function () {
          i.c(arguments);
        };
        i.q = [];
        i.c = function (args) {
          i.q.push(args);
        };
        w.Intercom = i;
        const l = function () {
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = `https://widget.intercom.io/widget/${process.env.INTERCOM_APP_ID}`;
          const x = d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
      /* eslint-enable */
    };
    onMounted(() => {
      if (!window.Intercom) {
        setTimeout(
          bootIntercom,
          process?.env?.NODE_ENV === 'development' ? 100 : 10000
        );
      }
      if (route.value.name === 'tv') {
        Cookie.set(CookieNames.MOUNTAIN_REFERRAL_CODE, 'MNTN');
        Cookie.set(CookieNames.MOUNTAIN_SOURCE, 'TV');
      }
      // If an A/B test exists in Netlify, then trigger an ab-test-entry on the homepage to be sent to segment and then picked up by the data team.
      // DO NOT REMOVE! This is needed to power dashboards! If needed, just relocate code.
      /*if (process.env.AB_TEST_NAME) {
        setTimeout(() => {
          window?.analytics?.track('ab-test-entry', {
            'test-name': process.env.AB_TEST_NAME,
            'test-variant': process.env.AB_TEST_VARIANT,
          });
        }, 1000);
      }*/
      const observer = new IntersectionObserver(
        function (entries) {
          // isIntersecting is true when element and viewport are overlapping
          // isIntersecting is false when element and viewport don't overlap
          if (!viewedPriceModel.value && entries[0].isIntersecting === true) {
            window?.analytics?.track('viewed-pricing-panel', {});
            viewedPriceModel.value = true; // This only allows the event to fired only once.
          }
        },
        { threshold: [0.5] }
      );
      observer.observe(document.querySelector('#pricing-plans'));
    });

    return {
      subHeroItems,
      Factoids,
    };
  },
  head() {
    return {
      meta: [
        { charset: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        {
          hid: 'description',
          vmid: 'description',
          name: 'description',
          content: META_DESCRIPTION,
        },
        { property: 'fb:app_id', content: '1048426768539865' },
        { property: 'og:type', content: 'website' },
        {
          hid: 'og:title',
          property: 'og:title',
          content: META_DESCRIPTION_SHORT,
        },
        {
          hid: 'og:description',
          vmid: 'og:description',
          property: 'og:description',
          content: META_DESCRIPTION,
        },
        {
          hid: 'og:image:type',
          property: 'og:image:type',
          content: 'image/png',
        },
      ],
    };
  },
});
</script>
